import * as THREE from 'three';
import { MAP } from '../utils/constants';
import { mulberry32 } from '../utils/rng';

export interface NPCVehicle {
  mesh: THREE.Group;
  x: number;
  z: number;
  vx: number;
  vz: number;
  heading: number;
  speed: number;
  targetSpeed: number;
  lane: number;
  roadAxis: 'x' | 'z';
  roadPosition: number;
  color: number;
  type: 'sedan' | 'suv' | 'truck' | 'sports';
  collisionRadius: number;
  destroyed: boolean;
}

export interface NPCTrafficHandle {
  group: THREE.Group;
  vehicles: NPCVehicle[];
  update: (dt: number, playerX: number, playerZ: number, copX: number, copZ: number) => void;
}

const NPC_COLORS = [
  0x1a1a2e, 0x16213e, 0x0f3460, 0x533483,
  0x8b0000, 0x2f4f4f, 0x191970, 0x006400,
  0x4a4a4a, 0x2c2c2c, 0x3d3d3d, 0x1f1f1f,
  0xc0c0c0, 0xa9a9a9, 0x808080, 0xdcdcdc,
  0x8b4513, 0x654321, 0x5c4033, 0x3b2f2f,
];

const VEHICLE_TYPES: Array<{ type: NPCVehicle['type']; w: number; h: number; l: number; weight: number; collisionRadius: number }> = [
  { type: 'sedan', w: 1.9, h: 1.3, l: 4.6, weight: 0.5, collisionRadius: 2.3 },
  { type: 'suv', w: 2.1, h: 1.6, l: 4.9, weight: 0.25, collisionRadius: 2.5 },
  { type: 'truck', w: 2.3, h: 2.0, l: 6.2, weight: 0.1, collisionRadius: 3.1 },
  { type: 'sports', w: 1.95, h: 1.15, l: 4.4, weight: 0.15, collisionRadius: 2.2 },
];

function pickVehicleType(rng: () => number): typeof VEHICLE_TYPES[0] {
  const r = rng();
  let acc = 0;
  for (const vt of VEHICLE_TYPES) {
    acc += vt.weight;
    if (r < acc) return vt;
  }
  return VEHICLE_TYPES[0];
}

function createNPCCarMesh(
  type: NPCVehicle['type'],
  color: number,
  vt: typeof VEHICLE_TYPES[0],
): THREE.Group {
  const group = new THREE.Group();
  
  const bodyMat = new THREE.MeshStandardMaterial({
    color,
    roughness: 0.3,
    metalness: 0.7,
    envMapIntensity: 0.8,
  });
  
  const glassMat = new THREE.MeshStandardMaterial({
    color: 0x1a1a2e,
    roughness: 0.1,
    metalness: 0.9,
    transparent: true,
    opacity: 0.7,
  });
  
  const wheelMat = new THREE.MeshStandardMaterial({
    color: 0x1a1a1a,
    roughness: 0.8,
    metalness: 0.2,
  });
  
  const { w, h, l } = vt;
  
  const body = new THREE.Mesh(
    new THREE.BoxGeometry(w, h * 0.5, l),
    bodyMat,
  );
  body.position.y = h * 0.25;
  body.castShadow = true;
  group.add(body);
  
  if (type !== 'truck') {
    const cabin = new THREE.Mesh(
      new THREE.BoxGeometry(w * 0.85, h * 0.4, l * 0.45),
      bodyMat,
    );
    cabin.position.set(0, h * 0.55, -l * 0.05);
    cabin.castShadow = true;
    group.add(cabin);
    
    const windshield = new THREE.Mesh(
      new THREE.BoxGeometry(w * 0.8, h * 0.35, 0.05),
      glassMat,
    );
    windshield.position.set(0, h * 0.55, l * 0.15);
    windshield.rotation.x = -0.2;
    group.add(windshield);
    
    const rearWindow = new THREE.Mesh(
      new THREE.BoxGeometry(w * 0.75, h * 0.3, 0.05),
      glassMat,
    );
    rearWindow.position.set(0, h * 0.55, -l * 0.22);
    rearWindow.rotation.x = 0.3;
    group.add(rearWindow);
  } else {
    const cabin = new THREE.Mesh(
      new THREE.BoxGeometry(w * 0.9, h * 0.6, l * 0.3),
      bodyMat,
    );
    cabin.position.set(0, h * 0.55, l * 0.25);
    cabin.castShadow = true;
    group.add(cabin);
    
    const bed = new THREE.Mesh(
      new THREE.BoxGeometry(w * 0.95, h * 0.3, l * 0.5),
      bodyMat,
    );
    bed.position.set(0, h * 0.35, -l * 0.2);
    bed.castShadow = true;
    group.add(bed);
  }
  
  const wheelGeo = new THREE.CylinderGeometry(h * 0.22, h * 0.22, w * 0.15, 12);
  wheelGeo.rotateZ(Math.PI / 2);
  
  const wheelPositions = [
    { x: w * 0.45, z: l * 0.32 },
    { x: -w * 0.45, z: l * 0.32 },
    { x: w * 0.45, z: -l * 0.32 },
    { x: -w * 0.45, z: -l * 0.32 },
  ];
  
  for (const pos of wheelPositions) {
    const wheel = new THREE.Mesh(wheelGeo, wheelMat);
    wheel.position.set(pos.x, h * 0.22, pos.z);
    wheel.castShadow = true;
    group.add(wheel);
  }
  
  const headlightMat = new THREE.MeshStandardMaterial({
    color: 0xffffee,
    emissive: 0xffffcc,
    emissiveIntensity: 0.3,
  });
  
  const headlightGeo = new THREE.BoxGeometry(w * 0.15, h * 0.1, 0.05);
  for (const side of [-1, 1]) {
    const hl = new THREE.Mesh(headlightGeo, headlightMat);
    hl.position.set(side * w * 0.35, h * 0.3, l * 0.5);
    group.add(hl);
  }
  
  const taillightMat = new THREE.MeshStandardMaterial({
    color: 0xff0000,
    emissive: 0xff0000,
    emissiveIntensity: 0.2,
  });
  
  const taillightGeo = new THREE.BoxGeometry(w * 0.12, h * 0.08, 0.05);
  for (const side of [-1, 1]) {
    const tl = new THREE.Mesh(taillightGeo, taillightMat);
    tl.position.set(side * w * 0.38, h * 0.3, -l * 0.5);
    group.add(tl);
  }
  
  return group;
}

function getRoadPositions(half: number, grid: number, roadWidth: number, blockW: number): Array<{
  axis: 'x' | 'z';
  position: number;
  lanes: number[];
}> {
  const roads: Array<{ axis: 'x' | 'z'; position: number; lanes: number[] }> = [];
  const roadWithSidewalk = roadWidth + MAP.SIDEWALK_WIDTH * 2;
  const laneOffset = roadWidth * 0.22;
  
  for (let i = 0; i <= grid; i++) {
    const pos = -half + i * (roadWithSidewalk + blockW) + roadWithSidewalk / 2;
    roads.push({
      axis: 'z',
      position: pos,
      lanes: [-laneOffset, laneOffset],
    });
  }
  
  for (let j = 0; j <= grid; j++) {
    const pos = -half + j * (roadWithSidewalk + blockW) + roadWithSidewalk / 2;
    roads.push({
      axis: 'x',
      position: pos,
      lanes: [-laneOffset, laneOffset],
    });
  }
  
  return roads;
}

export function createNPCTraffic(seed: number = 42, count: number = 40): NPCTrafficHandle {
  const group = new THREE.Group();
  group.name = 'NPCTraffic';
  
  const rng = mulberry32(seed);
  const vehicles: NPCVehicle[] = [];
  
  const { HALF, GRID, ROAD_WIDTH, SIDEWALK_WIDTH } = MAP;
  const roadWithSidewalk = ROAD_WIDTH + SIDEWALK_WIDTH * 2;
  const totalRoadSpace = (GRID + 1) * roadWithSidewalk;
  const inner = MAP.SIZE - totalRoadSpace;
  const blockW = inner / GRID;
  
  const roads = getRoadPositions(HALF, GRID, ROAD_WIDTH, blockW);
  
  for (let i = 0; i < count; i++) {
    const road = roads[Math.floor(rng() * roads.length)];
    const laneIdx = Math.floor(rng() * road.lanes.length);
    const lane = road.lanes[laneIdx];
    const direction = laneIdx === 0 ? 1 : -1;
    
    let x: number, z: number, heading: number;
    const alongRoad = -HALF + rng() * MAP.SIZE;
    
    if (road.axis === 'z') {
      x = alongRoad;
      z = road.position + lane;
      heading = direction > 0 ? 0 : Math.PI;
    } else {
      x = road.position + lane;
      z = alongRoad;
      heading = direction > 0 ? Math.PI / 2 : -Math.PI / 2;
    }
    
    const color = NPC_COLORS[Math.floor(rng() * NPC_COLORS.length)];
    const vt = pickVehicleType(rng);
    const mesh = createNPCCarMesh(vt.type, color, vt);
    mesh.position.set(x, 0, z);
    mesh.rotation.y = heading;
    group.add(mesh);
    
    const baseSpeed = 8 + rng() * 12;
    
    vehicles.push({
      mesh,
      x,
      z,
      vx: 0,
      vz: 0,
      heading,
      speed: baseSpeed,
      targetSpeed: baseSpeed,
      lane: laneIdx,
      roadAxis: road.axis,
      roadPosition: road.position,
      color,
      type: vt.type,
      collisionRadius: vt.collisionRadius,
      destroyed: false,
    });
  }
  
  function update(dt: number, playerX: number, playerZ: number, copX: number, copZ: number): void {
    for (const v of vehicles) {
      const dirX = Math.sin(v.heading);
      const dirZ = Math.cos(v.heading);
      
      let targetSpeed = v.targetSpeed;
      
      const lookAhead = v.speed * 1.5 + 10;
      const futureX = v.x + dirX * lookAhead;
      const futureZ = v.z + dirZ * lookAhead;
      
      const playerDist = Math.hypot(futureX - playerX, futureZ - playerZ);
      if (playerDist < 15) {
        targetSpeed = Math.min(targetSpeed, playerDist * 0.5);
      }
      
      const copDist = Math.hypot(futureX - copX, futureZ - copZ);
      if (copDist < 15) {
        targetSpeed = Math.min(targetSpeed, copDist * 0.5);
      }
      
      for (const other of vehicles) {
        if (other === v) continue;
        const dist = Math.hypot(futureX - other.x, futureZ - other.z);
        if (dist < 12) {
          const dot = dirX * Math.sin(other.heading) + dirZ * Math.cos(other.heading);
          if (dot > 0.5) {
            targetSpeed = Math.min(targetSpeed, other.speed * 0.8, dist * 0.8);
          }
        }
      }
      
      const accel = targetSpeed > v.speed ? 8 : 15;
      v.speed += (targetSpeed - v.speed) * Math.min(1, accel * dt);
      v.speed = Math.max(0, v.speed);
      
      v.vx = dirX * v.speed;
      v.vz = dirZ * v.speed;
      
      v.x += v.vx * dt;
      v.z += v.vz * dt;
      
      const margin = 50;
      if (v.x < -HALF - margin) v.x = HALF + margin * 0.5;
      if (v.x > HALF + margin) v.x = -HALF - margin * 0.5;
      if (v.z < -HALF - margin) v.z = HALF + margin * 0.5;
      if (v.z > HALF + margin) v.z = -HALF - margin * 0.5;
      
      v.mesh.position.set(v.x, 0, v.z);
      v.mesh.rotation.y = v.heading;
    }
  }
  
  return { group, vehicles, update };
}
